import React from 'react';
import styles from "../../../styles/applicant/ApplicantTable.module.css";
import { FaFileDownload, FaChevronDown } from "react-icons/fa";

const STATUS_BADGE_CLASSES = {
    approvemanully: styles.approved,
    approveautomatically: styles.approved,
    approved: styles.approved,
    rejected: styles.rejected,
    reassigned: styles.rejected,
    pending: styles.pending,
    sendbackforreview: styles.sendbackforreview,
    sendforauthorization: styles.sendforauthorization
};

const TableBody = ({
    currentConfig,
    columnVisibility,
    sortConfig,
    requestSort,
    displayApplications,
    language,
    formatDate,
    openApplicationDetails
}) => {
    const getStatusBadgeClass = (status) => STATUS_BADGE_CLASSES[status.toLowerCase()] || styles.pending;

    const handleCellClick = (key, application) => {
        if (key === 'request_unique_id') {
            openApplicationDetails(application);
        }
    };

    const getCellClass = (key) => {
        if (key === 'request_unique_id') return styles.clickableCell;
        return '';
    };

    const renderTableHeader = () => (
        <thead className={styles.theadCss}>
            <tr>
                <th>#</th>
                {Object.entries(currentConfig.columns).map(([key, config]) =>
                    columnVisibility[key] && (
                        <th
                            key={key}
                            className={config.sortable ? styles.sortable : ''}
                            onClick={config.sortable ? () => requestSort(key) : undefined}
                        >
                            {config.label}
                            {config.sortable && sortConfig.key === key && (
                                <span className={`${styles.sortIcon} ${sortConfig.direction === 'asc' ? '' : styles.asc}`}>
                                    <FaChevronDown size={12} />
                                </span>
                            )}
                        </th>
                    )
                )}
            </tr>
        </thead>
    );

    const renderTableRows = () => (
        <tbody>
            {displayApplications.map((application, index) => (
                <tr key={application.request_unique_id || `row-${index}`} className={styles.tableRow}>
                    <td>{index + 1}</td>
                    {Object.keys(currentConfig.columns).map(key =>
                        columnVisibility[key] && (
                            <TableCell
                                key={key}
                                columnKey={key}
                                application={application}
                                language={language}
                                formatDate={formatDate}
                                getStatusBadgeClass={getStatusBadgeClass}
                                handleCellClick={handleCellClick}
                                getCellClass={getCellClass}
                            />
                        )
                    )}
                </tr>
            ))}
        </tbody>
    );

    return (
        <>
            {renderTableHeader()}
            {renderTableRows()}
        </>
    );
};

const TableCell = ({
    columnKey,
    application,
    language,
    formatDate,
    getStatusBadgeClass,
    handleCellClick,
    getCellClass
}) => {
    const value = application[columnKey];
    const isEn = language === 'en';

    const renderCellContent = () => {
        switch (columnKey) {
            case 'dateOfIssue':
            case 'dateOfAction':
                return formatDate(value);

            case 'satbaraAttachment':
            case 'certificatePath':
                return renderFileLink(value, application, columnKey, isEn);

            case 'certificatename':
                return getCertificateName(value, isEn);

            case 'meausurementAttachment':
                return renderMeasurementAttachment(value, isEn);

            case 'status':
                return renderStatusBadge(value, application, isEn, getStatusBadgeClass);

            case 'confirm1':
            case 'confirm2':
                return value ? (isEn ? "Yes" : "होय") : (isEn ? "No" : "नाही");

            case 'feeReceipt':
                return value ? (isEn ? "Paid" : "पेड") : (isEn ? "Pending" : "प्रलंबित");

            default:
                return value;
        }
    };

    return (
        <td
            onClick={() => handleCellClick(columnKey, application)}
            className={getCellClass(columnKey)}
        >
            {renderCellContent()}
        </td>
    );
};

const renderFileLink = (value, application, columnKey, isEn) => {
    if (value) {
        return (
            <a
                href={`${process.env.REACT_APP_SERVER_URL}/${value}`}
                download
                className={styles.fileLink}
            >
                <FaFileDownload /> {getFileLinkText(columnKey, isEn)}
            </a>
        );
    }
    
    return application.approved_m_or_a === 'automatic' ? (
        <a className={styles.fileLink}>
            <FaFileDownload /> {getFileLinkText(columnKey, isEn)}
        </a>
    ) : "N/A";
};

const getFileLinkText = (columnKey, isEn) => {
    if (columnKey === 'certificatePath') {
        return isEn ? 'Document' : 'प्रमाणपत्र';
    }
    return isEn ? 'Download' : 'डाउनलोड';
};

const getCertificateName = (value, isEn) => {
    if (value === 'Zone') return isEn ? 'Zone' : 'झोन';
    if (value === 'Part Plan') return isEn ? 'Part Plan' : 'भाग योजना';
    return value;
};

const renderMeasurementAttachment = (value, isEn) => {
    if (value) {
        return (
            <a
                href={`${process.env.REACT_APP_SERVER_URL}/${value}`}
                download
                className={styles.fileLink}
            >
                <FaFileDownload /> {isEn ? 'Download' : 'डाउनलोड'}
            </a>
        );
    }
    return "N/A";
};

const renderStatusBadge = (value, application, isEn, getStatusBadgeClass) => {
    const statusLabel = getStatusLabel(value, isEn);
    
    return (
        <span className={`${styles.statusBadge} ${getStatusBadgeClass(value)}`}>
            {statusLabel}
            {value === "approved" && application.certificatePath && (
                <a
                    href={`${process.env.REACT_APP_SERVER_URL}/${application.certificatePath}`}
                    download
                    className={styles.fileLinkDownloadIcon}
                >
                    <FaFileDownload />
                </a>
            )}
        </span>
    );
};

const getStatusLabel = (value, isEn) => {
    if (isEn) {
        const statusMap = {
            "pending": "Pending",
            "approved": "Approved",
            "rejected": "Rejected",
            "SendBackforReview": "Send Back for Review",
            "SendforAuthorization": "Sent for Authorization",
            "sendforauthorizationmanully": "Sent for Authorization",
            "sendforauthorizationautomatically": "Sent for Authorization",
            "reassigned": "Reassigned",
            "approveautomatically": "Approved",
            "approvemanully": "Approved"
        };
        return statusMap[value] || "Unknown";
    } else {
        const statusMap = {
            "pending": "प्रलंबित",
            "approved": "मंजूर",
            "rejected": "नाकारले",
            "SendBackforReview": "पुनरावलोकनासाठी परत पाठवले",
            "SendforAuthorization": "प्राधिकरणासाठी पाठवले"
        };
        return statusMap[value] || "अज्ञात";
    }
};

export default TableBody;